// DesignTokenIntegration.js
// Integration with _ui_upgrade design standards and tokens
// Ensures consistency with the comprehensive component library

/**
 * Design token integration system
 * Provides standardized design tokens from _ui_upgrade specifications
 * Ensures consistent styling across all components
 */
export class DesignTokenIntegration {
    static _instance = null;
    static _tokensLoaded = false;
    
    /**
     * Singleton pattern for consistent design token management
     */
    static getInstance() {
        if (!DesignTokenIntegration._instance) {
            DesignTokenIntegration._instance = new DesignTokenIntegration();
        }
        return DesignTokenIntegration._instance;
    }
    
    constructor() {
        if (DesignTokenIntegration._instance) {
            return DesignTokenIntegration._instance;
        }
        
        this._injectDesignTokens();
        DesignTokenIntegration._instance = this;
    }
    
    /**
     * Inject _ui_upgrade design tokens as CSS variables
     * @private
     */
    _injectDesignTokens() {
        if (DesignTokenIntegration._tokensLoaded) return;
        
        const designTokenCSS = `
            <style id="ui-upgrade-design-tokens">
                /* _ui_upgrade Design Tokens - MANDATORY STANDARDS */
                :root {
                    /* Primary Brand Colors - EXACT from _ui_upgrade */
                    --primary: #00d4ff;
                    --primary-dark: #0ea5e9;
                    
                    /* Background System - EXACT from _ui_upgrade */
                    --background: #0f172a;
                    --background-secondary: #1e293b;
                    --background-tertiary: #334155;
                    
                    /* Glass Morphism - EXACT from _ui_upgrade */
                    --glass-bg: rgba(30, 41, 59, 0.8);  /* Mobile */
                    --glass-bg-desktop: rgba(30, 41, 59, 0.6);  /* Desktop */
                    --glass-bg-light: rgba(255, 255, 255, 0.05);
                    --glass-border: rgba(255, 255, 255, 0.1);
                    --glass-blur: 20px;  /* Standardized blur */
                    --glass-shadow: 0 25px 50px rgba(0, 0, 0, 0.2);
                    --glass-shadow-sm: 0 10px 20px rgba(0, 0, 0, 0.15);
                    
                    /* Text Hierarchy - EXACT from _ui_upgrade */
                    --text-primary: #ffffff;
                    --text-secondary: rgba(255, 255, 255, 0.7);
                    --text-muted: rgba(148, 163, 184, 1);
                    --text-disabled: rgba(255, 255, 255, 0.3);
                    
                    /* Status Colors - EXACT from _ui_upgrade */
                    --success: #10b981;
                    --error: #ef4444;
                    --warning: #f59e0b;
                    --info: #3b82f6;
                    --purple: #8b5cf6;
                    
                    /* Typography Scale - EXACT from _ui_upgrade (8px Grid) */
                    --font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                    
                    /* Mobile Typography */
                    --font-size-mobile-xs: 12px;
                    --font-size-mobile-sm: 14px;
                    --font-size-mobile-base: 16px;
                    --font-size-mobile-lg: 18px;
                    --font-size-mobile-xl: 24px;
                    --font-size-mobile-2xl: 32px;
                    
                    /* Desktop Typography */
                    --font-size-desktop-xs: 11px;
                    --font-size-desktop-sm: 13px;
                    --font-size-desktop-base: 15px;
                    --font-size-desktop-lg: 18px;
                    --font-size-desktop-xl: 24px;
                    --font-size-desktop-2xl: 32px;
                    --font-size-desktop-3xl: 48px;
                    
                    /* Font Weights - EXACT from _ui_upgrade */
                    --font-weight-regular: 400;
                    --font-weight-medium: 500;
                    --font-weight-semibold: 600;
                    --font-weight-bold: 700;
                    --font-weight-extrabold: 800;
                    
                    /* Spacing System - EXACT from _ui_upgrade (8px Grid) */
                    --space-xs: 4px;
                    --space-sm: 8px;
                    --space-md: 16px;
                    --space-lg: 24px;
                    --space-xl: 32px;
                    --space-2xl: 48px;
                    --space-3xl: 64px;
                    
                    /* Border Radius - EXACT from _ui_upgrade */
                    --radius-sm: 6px;
                    --radius-md: 12px;
                    --radius-lg: 20px;
                    --radius-xl: 24px;
                    --radius-full: 9999px;
                    
                    /* Transitions - EXACT from _ui_upgrade */
                    --transition-fast: 150ms ease;
                    --transition-base: 300ms ease;
                    --transition-slow: 500ms ease;
                    
                    /* Z-Index Scale - EXACT from _ui_upgrade */
                    --z-base: 0;
                    --z-dropdown: 100;
                    --z-modal: 200;
                    --z-notification: 300;
                    --z-tooltip: 400;
                    
                    /* Layout Constants */
                    --sidebar-width: 280px;
                    --header-height: 70px;
                }
                
                /* _ui_upgrade Component Base Classes */
                
                /* Glass Morphism Card - EXACT from component library */
                .glass-card {
                    background: var(--glass-bg);
                    backdrop-filter: blur(var(--glass-blur));
                    -webkit-backdrop-filter: blur(var(--glass-blur));
                    border: 1px solid var(--glass-border);
                    border-radius: var(--radius-lg);
                    box-shadow: var(--glass-shadow-sm);
                    transition: all var(--transition-base);
                }
                
                .glass-card:hover {
                    transform: translateY(-4px);
                    box-shadow: var(--glass-shadow);
                    border-color: var(--primary);
                }
                
                /* Professional Button - EXACT from component library */
                .btn {
                    padding: 12px 24px;
                    border: none;
                    border-radius: var(--radius-md);
                    font-size: var(--font-size-desktop-base);
                    font-weight: var(--font-weight-semibold);
                    cursor: pointer;
                    transition: all var(--transition-base);
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: var(--space-sm);
                    position: relative;
                    overflow: hidden;
                }
                
                .btn-primary {
                    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
                    color: var(--background);
                    box-shadow: 0 4px 20px rgba(0, 212, 255, 0.3);
                }
                
                .btn-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 30px rgba(0, 212, 255, 0.4);
                }
                
                .btn-secondary {
                    background: var(--glass-bg-light);
                    color: var(--text-primary);
                    border: 1px solid var(--glass-border);
                    backdrop-filter: blur(10px);
                    -webkit-backdrop-filter: blur(10px);
                }
                
                .btn-secondary:hover {
                    background: rgba(255, 255, 255, 0.1);
                    border-color: var(--primary);
                }
                
                /* Form Input - EXACT from component library */
                .form-input {
                    width: 100%;
                    padding: 12px 16px;
                    background: var(--glass-bg-light);
                    border: 1px solid var(--glass-border);
                    border-radius: var(--radius-md);
                    color: var(--text-primary);
                    font-size: var(--font-size-desktop-base);
                    transition: all var(--transition-base);
                }
                
                .form-input:focus {
                    outline: none;
                    background: rgba(255, 255, 255, 0.08);
                    border-color: var(--primary);
                    box-shadow: 0 0 0 3px rgba(0, 212, 255, 0.1);
                }
                
                /* Progress Bar - EXACT from component library */
                .progress {
                    width: 100%;
                    height: 8px;
                    background: var(--glass-bg-light);
                    border-radius: var(--radius-full);
                    overflow: hidden;
                }
                
                .progress-bar {
                    height: 100%;
                    background: linear-gradient(90deg, var(--primary) 0%, var(--primary-dark) 100%);
                    border-radius: var(--radius-full);
                    transition: width var(--transition-base);
                }
                
                /* Notification - EXACT from component library */
                .notification {
                    display: flex;
                    align-items: flex-start;
                    gap: var(--space-md);
                    padding: var(--space-md);
                    background: var(--glass-bg);
                    backdrop-filter: blur(var(--glass-blur));
                    -webkit-backdrop-filter: blur(var(--glass-blur));
                    border: 1px solid var(--glass-border);
                    border-radius: var(--radius-md);
                    margin-bottom: var(--space-md);
                    animation: slideInRight var(--transition-base);
                }
                
                @keyframes slideInRight {
                    from {
                        transform: translateX(100%);
                        opacity: 0;
                    }
                    to {
                        transform: translateX(0);
                        opacity: 1;
                    }
                }
                
                /* Skeleton Loading - EXACT from component library */
                .skeleton {
                    background: linear-gradient(90deg, 
                        var(--glass-bg-light) 25%, 
                        rgba(255, 255, 255, 0.1) 50%, 
                        var(--glass-bg-light) 75%);
                    background-size: 200% 100%;
                    animation: skeletonLoading 1.5s infinite;
                    border-radius: var(--radius-sm);
                }
                
                @keyframes skeletonLoading {
                    0% {
                        background-position: 200% 0;
                    }
                    100% {
                        background-position: -200% 0;
                    }
                }
                
                /* Touch Target Optimization - EXACT from _ui_upgrade */
                @media (max-width: 768px) {
                    .btn, .nav-item, [data-action], [data-game] {
                        min-width: 48px;
                        min-height: 48px;
                    }
                }
                
                /* Accessibility - EXACT from _ui_upgrade */
                .focus-visible {
                    outline: 2px solid var(--primary) !important;
                    outline-offset: 2px !important;
                    box-shadow: 0 0 0 4px rgba(0, 212, 255, 0.2) !important;
                }
                
                /* Reduced Motion Support - EXACT from _ui_upgrade */
                @media (prefers-reduced-motion: reduce) {
                    *, *::before, *::after {
                        animation-duration: 0.01ms !important;
                        animation-iteration-count: 1 !important;
                        transition-duration: 0.01ms !important;
                    }
                }
                
                /* High Contrast Mode - EXACT from _ui_upgrade */
                @media (prefers-contrast: high) {
                    .btn, .nav-item, .glass-card {
                        border: 2px solid currentColor;
                    }
                    
                    .focus-visible {
                        outline-width: 3px !important;
                    }
                }
            </style>
        `;
        
        // Insert at the beginning of head for highest priority
        document.head.insertBefore(
            document.createRange().createContextualFragment(designTokenCSS).firstElementChild,
            document.head.firstChild
        );
        
        DesignTokenIntegration._tokensLoaded = true;
        console.log('🎨 _ui_upgrade design tokens integrated successfully');
    }
    
    /**
     * Apply _ui_upgrade component classes to existing elements
     * @param {Element} container - Container to apply classes to
     */
    static applyComponentClasses(container) {
        if (!container) return;
        
        // Convert existing buttons to _ui_upgrade standards
        const buttons = container.querySelectorAll('button:not(.btn)');
        buttons.forEach(button => {
            button.classList.add('btn');
            
            // Determine button type based on existing classes/content
            if (button.classList.contains('export-btn') || button.textContent.includes('Export')) {
                button.classList.add('btn-secondary');
            } else if (button.classList.contains('time-button') || button.classList.contains('filter-tab')) {
                button.classList.add('btn-secondary');
            } else {
                button.classList.add('btn-primary');
            }
        });
        
        // Convert existing cards to _ui_upgrade standards
        const cards = container.querySelectorAll('.kpi-card, .chart-card, .game-card, .stats-card, .leaderboard-card');
        cards.forEach(card => {
            card.classList.add('glass-card');
        });
        
        // Convert existing inputs to _ui_upgrade standards
        const inputs = container.querySelectorAll('input:not(.form-input), select:not(.form-input)');
        inputs.forEach(input => {
            input.classList.add('form-input');
        });
        
        // Convert existing progress bars to _ui_upgrade standards
        const progressBars = container.querySelectorAll('.progress-fill, .bar-fill');
        progressBars.forEach(bar => {
            bar.classList.add('progress-bar');
            if (!bar.parentElement.classList.contains('progress')) {
                bar.parentElement.classList.add('progress');
            }
        });
        
        console.log('✅ _ui_upgrade component classes applied to container');
    }
    
    /**
     * Get design token value
     * @param {string} tokenName - Token name (without --)
     * @returns {string} - Token value
     */
    static getToken(tokenName) {
        return getComputedStyle(document.documentElement).getPropertyValue(`--${tokenName}`).trim();
    }
    
    /**
     * Set design token value
     * @param {string} tokenName - Token name (without --)
     * @param {string} value - Token value
     */
    static setToken(tokenName, value) {
        document.documentElement.style.setProperty(`--${tokenName}`, value);
    }
    
    /**
     * Validate design token compliance
     * @param {Element} container - Container to validate
     * @returns {Object} - Validation results
     */
    static validateCompliance(container) {
        const issues = [];
        
        // Check for hardcoded colors
        const elementsWithInlineStyles = container.querySelectorAll('[style*="color"], [style*="background"]');
        if (elementsWithInlineStyles.length > 0) {
            issues.push(`${elementsWithInlineStyles.length} elements with hardcoded colors`);
        }
        
        // Check for missing component classes
        const buttons = container.querySelectorAll('button:not(.btn)');
        if (buttons.length > 0) {
            issues.push(`${buttons.length} buttons missing .btn class`);
        }
        
        const cards = container.querySelectorAll('.kpi-card:not(.glass-card), .chart-card:not(.glass-card)');
        if (cards.length > 0) {
            issues.push(`${cards.length} cards missing .glass-card class`);
        }
        
        return {
            compliant: issues.length === 0,
            issues: issues
        };
    }
}

// Auto-initialize design tokens
DesignTokenIntegration.getInstance();

export default DesignTokenIntegration;

